import * as React from 'react';
import { isNil, noop } from 'lodash';
import moment from 'moment';

import ActionButton from 'components/buttons/action-button/ActionButton';
import { withModal } from 'components/modal/ModalHOC';
import StatusLabel from 'components/status-label/StatusLabel';
import Popover from 'components/popover/Popover';

import CampaignPayoutsReleaseModal from '../../release/CampaignPayoutsReleaseModal';

import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { getDisplayDate } from 'utils/date';
import {
  getExpectedPayoutDate,
  getExpectedPayoutDateWithRelationToReleaseStatus
} from 'utils/payouts';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./campaign-payouts-list-item.i18n.json'));

import type { PaymentConfirmationModalParams, PayoutWithHyphenAmount, Subcampaign } from 'types';

const ActionButtonWithModalRelease = withModal(ActionButton, {
  modalContent: CampaignPayoutsReleaseModal,
  width: 'small'
});

type Props = {
  payout: PayoutWithHyphenAmount,
  position: number,
  campaignId: number,
  subcampaign: Subcampaign,
  isBrand: boolean,
  isNegotiationReopened?: boolean,
  canReleasePayout?: (p: PayoutWithHyphenAmount) => boolean,
  onPayoutRelease?: () => void,
  onUnreleasePartialPayout?: (payoutId: number, campaignId: number, subcampaignId: number) => void,
  onShowConfirmationModal?: (params: PaymentConfirmationModalParams) => void,
  onHideConfirmationModal?: () => void
};

class CampaignPayoutsListItem extends React.PureComponent<Props> {
  static defaultProps = {
    isBrand: false
  };

  handleUnrelease = (): void => {
    const { onUnreleasePartialPayout, onShowConfirmationModal, onHideConfirmationModal } = this.props;
    const unreleasePayout = onUnreleasePartialPayout || noop;
    const showModal = onShowConfirmationModal || noop;
    const hideModal = onHideConfirmationModal || noop;

    showModal({
      title: __t('campaign_payouts_list_item.modals.confirmation.title'),
      message: (
        <div className="campaign-payouts-list-item__modal-message">
          {__t('campaign_payouts_list_item.modals.confirmation.message')}
        </div>
      ),
      onCancel: () => hideModal(),
      onClose: () => hideModal(),
      onConfirm: () => {
        const { payout, campaignId, subcampaign } = this.props;

        if (payout.id) {
          hideModal();
          unreleasePayout(payout.id, campaignId, subcampaign.id);
        } else {
          throw new Error(__t('campaign_payouts_list_item.errors.unrelease.payout_id_not_found'));
        }
      }
    });
  };

  renderInfluencerItem(): React.ReactNode {
    const { payout, position } = this.props;
    const payoutDate = payout.releaseDate
      ? getExpectedPayoutDateWithRelationToReleaseStatus(new Date(), payout.releaseDate, true)
      : undefined;

    return (
      <div
        style={{
          order: moment(payoutDate).unix()
        }}
        key={`campaign-payouts-list-item-${String(payout.id)}`}
        className="campaign-payouts-list-item campaign-payouts-list-item-type-influencer"
      >
        <div className="campaign-payouts-list-item__column">
          {
            typeof payout.amount === 'string'
              ? payout.amount
              : renderPrice(payout.amount)
          }
        </div>
        <div className="campaign-payouts-list-item__column">
          {
            payoutDate
              ? getDisplayDate(payoutDate)
              : __t('campaign_payouts_list_item.labels.not_yet_released')
          }
        </div>
        <div className="campaign-payouts-list-item__column">
          {
            payout.executed && (
              <StatusLabel
                label={__t('campaign_payouts_list_item.labels.paid')}
                size="small"
                data={{ qa: `campaign-payouts-list-item__status-label--paid-${position}` }}
              />
            )
          }
          {
            !payout.executed && payout.releaseDate && (
              <StatusLabel
                type="warning"
                label={__t('campaign_payouts_list_item.labels.pending')}
                size="small"
                data={{ qa: `campaign-payouts-list-item__status-label--pending-${position}` }}
              />
            )
          }
        </div>
      </div>
    );
  }

  renderActionButtonWithModalRelease(): React.ReactNode {
    const {
      campaignId,
      subcampaign,
      payout,
      position,
      isNegotiationReopened,
      canReleasePayout,
      onPayoutRelease
    } = this.props;

    return (
      <ActionButtonWithModalRelease
        data={{ qa: `campaign-payouts-list-item__button-release-${position}` }}
        modalContentProps={{
          influencerProfile: subcampaign.creatorProfile,
          payoutId: payout.id,
          campaignId,
          subcampaignId: subcampaign.id,
          totalBudget: payout.amount,
          isNegotiationReopened,
          onPayoutRelease
        }}
        disabled={!isNil(canReleasePayout) && !canReleasePayout(payout)}
      >
        {__t('campaign_payouts_list_item.buttons.release')}
      </ActionButtonWithModalRelease>
    );
  }

  renderUnreleaseLink(): React.ReactNode {
    return (
      <div
        className="campaign-payouts-list-item__unrelease"
        onClick={this.handleUnrelease}
      >
        <Popover
          classNamePopover="campaign-payouts-list-item__popover"
          triggerComponent={(
            <div>
              [
              <span>
                {__t('campaign_payouts_list_item.buttons.unrelease')}
              </span>
              ]
            </div>
          )}
        >
          <div className="campaign-payouts-list-item__popover">
            <div>
              {__t('campaign_payouts_list_item.popovers.unrelease.one')}
            </div>
            <div>
              {__t('campaign_payouts_list_item.popovers.unrelease.two')}
            </div>
            <div>
              {__t('campaign_payouts_list_item.popovers.unrelease.three')}
            </div>
          </div>
        </Popover>
      </div>
    );
  }

  renderBrandItemStatus(): React.ReactNode | undefined | null {
    const { payout, position } = this.props;

    if (payout.executed) {
      return (
        <StatusLabel
          label={__t('campaign_payouts_list_item.labels.paid')}
          size="small"
          data={{ qa: `campaign-payouts-list-item__status-label--paid-${position}` }}
        />
      );
    }

    if (isNil(payout.releaseDate)) {
      return this.renderActionButtonWithModalRelease();
    }

    // COLLAB-16274
    const mScheduledDateMinusOneDay = moment(payout.scheduledDate).subtract(1, 'days');
    const mToday = moment();

    if (moment().isAfter(payout.releaseDate)) {
      return (
        <div className="campaign-payouts-list-item__status">
          <div>
            {__t('campaign_payouts_list_item.labels.released')}
          </div>
          {
            mToday.isSameOrBefore(mScheduledDateMinusOneDay)
              ? this.renderUnreleaseLink()
              : null
          }
        </div>
      );
    }

    return null;
  }

  renderBrandItem(): React.ReactNode {
    const { payout } = this.props;

    return (
      <div
        style={{
          order: moment(payout.scheduledDate).unix()
        }}
        key={`campaign-payouts-list-item-${String(payout.id)}`}
        className="campaign-payouts-list-item campaign-payouts-list-item-type-brand"
      >
        <div className="campaign-payouts-list-item__column">
          {payout.note}
          {
            payout.originalReleaseDate && payout.releaseDate !== payout.originalReleaseDate
              ? (
                <div className="campaign-payouts-list-item__column-note">
                  {__t('campaign_payouts_list_item.postponed_payout_note', {
                    date: getDisplayDate(getExpectedPayoutDate(payout.originalReleaseDate))
                  })}
                </div>
              ) : null
          }
        </div>
        <div className="campaign-payouts-list-item__column">
          {
            typeof payout.amount === 'string'
              ? payout.amount
              : renderPrice(payout.amount)
          }
        </div>
        <div className="campaign-payouts-list-item__column">
          {getDisplayDate(payout.scheduledDate)}
        </div>
        <div className="campaign-payouts-list-item__column">
          {getDisplayDate(payout.originalReleaseDate) || <span>&mdash;</span>}
        </div>
        <div className="campaign-payouts-list-item__column">
          {
            payout.releaseDate
              ? getDisplayDate(
                getExpectedPayoutDateWithRelationToReleaseStatus(
                  new Date(), payout.releaseDate, true
                )
              )
              : <span>&mdash;</span>
          }
        </div>
        <div className="campaign-payouts-list-item__column">
          {this.renderBrandItemStatus()}
        </div>
      </div>
    );
  }

  render(): React.ReactNode {
    const { isBrand } = this.props;

    if (isBrand) {
      return this.renderBrandItem();
    }

    return this.renderInfluencerItem();
  }
}

export default CampaignPayoutsListItem;
